/**
 * SeoHead.jsx – dynamische SEO-Meta-Tags pro Route
 * Installation: yarn add react-helmet-async
 * Dann in index.js: <HelmetProvider> um <App /> wrappen
 */
import React from "react";
import { Helmet } from "react-helmet-async";

const SITE_NAME = "Liesegang Consulting";
const DEFAULT_IMAGE = "https://liesegang-consulting.com/og-image.jpg";

export default function SeoHead({ title, description, canonical, image = DEFAULT_IMAGE, type = "website", lang = "de" }) {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `IT-Berater Ingolstadt München | ${SITE_NAME} – IT-Architektur · KI · Automotive`;

  const metaDesc = description ||
    "Stefan Liesegang – freiberuflicher IT-Berater Ingolstadt/München. IT-Architektur, Connected Car, eSIM, KI-Strategie, Projektmanagement für Automotive & Telco. 20+ Jahre Erfahrung.";

  const url = canonical || "https://liesegang-consulting.com/";

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === "de" ? "de_DE" : "en_GB"} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
}

export const SEO_CONFIG = {
  home: {
    de: {
      title: "IT-Berater Ingolstadt München | IT-Architektur · KI · Automotive",
      description: "Stefan Liesegang – freiberuflicher IT-Berater Ingolstadt/München. IT-Architektur, Connected Car, eSIM, KI-Strategie und Projektmanagement im Automotive- & Telco-Umfeld. 20+ Jahre Erfahrung.",
      canonical: "https://liesegang-consulting.com/",
    },
    en: {
      title: "IT Consultant Ingolstadt Munich | IT Architecture · AI · Automotive",
      description: "Stefan Liesegang – freelance IT consultant Ingolstadt/Munich. IT architecture, connected car, eSIM, AI strategy and project management for automotive & telco. 20+ years of experience.",
      canonical: "https://liesegang-consulting.com/",
    },
  },
  services: {
    "it-architektur": {
      de: {
        title: "IT-Architektur & Systemintegration | Freelancer Automotive München",
        description: "IT-Architektur für Connected Car, Cloud-native Plattformen und eSIM-Backend-Systeme. Freiberuflicher IT-Architekt aus Ingolstadt/München für Automotive und Telekommunikation.",
        canonical: "https://liesegang-consulting.com/leistungen/it-architektur",
      },
      en: {
        title: "IT Architecture & System Integration | Freelancer Automotive Munich",
        description: "IT architecture for connected car, cloud-native platforms and eSIM backend systems. Freelance IT architect from Ingolstadt/Munich for automotive and telecommunications.",
        canonical: "https://liesegang-consulting.com/leistungen/it-architektur",
      },
    },
    "projekt-testmanagement": {
      de: {
        title: "Projekt- & Testmanagement Freelancer | Connected Car Automotive",
        description: "Erfahrener Projektmanager für Connected-Car-Rollouts und internationale IT-Projekte im Automotive- und Telco-Umfeld. Testmanagement, QA-Strategie. Ingolstadt/München.",
        canonical: "https://liesegang-consulting.com/leistungen/projekt-testmanagement",
      },
      en: {
        title: "Project & Test Management Freelancer | Connected Car Automotive",
        description: "Experienced project manager for connected car rollouts and international IT projects in automotive and telco. Test management and QA strategy. Based in Ingolstadt/Munich.",
        canonical: "https://liesegang-consulting.com/leistungen/projekt-testmanagement",
      },
    },
    "business-analyse": {
      de: {
        title: "Business Analyse & Prozessoptimierung | Lean Six Sigma SAFe Automotive",
        description: "Business Analyse, Anforderungsmanagement und Prozessoptimierung mit Lean Six Sigma, Scrum und SAFe für Automotive und Telekommunikation. Freelancer Ingolstadt/München.",
        canonical: "https://liesegang-consulting.com/leistungen/business-analyse",
      },
      en: {
        title: "Business Analysis & Process Optimisation | Lean Six Sigma SAFe Automotive",
        description: "Business analysis, requirements management and process optimisation with Lean Six Sigma, Scrum and SAFe for automotive and telecommunications. Freelancer Ingolstadt/Munich.",
        canonical: "https://liesegang-consulting.com/leistungen/business-analyse",
      },
    },
    "ki-beratung": {
      de: {
        title: "KI-Beratung München Ingolstadt | KI-Strategie Automotive MLOps EU AI Act",
        description: "KI-Strategie, Use-Case-Mapping, MLOps und EU-AI-Act-Compliance für Automotive und Telekommunikation. Freiberuflicher KI-Berater aus Ingolstadt/München mit 20+ Jahren Erfahrung.",
        canonical: "https://liesegang-consulting.com/leistungen/ki-beratung",
      },
      en: {
        title: "AI Consulting Munich Ingolstadt | AI Strategy Automotive MLOps EU AI Act",
        description: "AI strategy, use-case mapping, MLOps and EU AI Act compliance for automotive and telecommunications. Freelance AI consultant from Ingolstadt/Munich with 20+ years of IT experience.",
        canonical: "https://liesegang-consulting.com/leistungen/ki-beratung",
      },
    },
  },
};
